import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Home,
  Bell,
  GraduationCap,
  Bot,
  FileText,
  Users,
  Video,
  Copy,
  CalendarDays,
  BarChart3,
  Layout,
  Mail,
  Settings,
  MessageSquare,
  LogOut,
  Shield,
  AlertTriangle,
  ChevronDown,
  Wrench,
  User,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { User } from "@supabase/supabase-js";
import { FEATURE_FLAGS } from "@/config/featureFlags";

interface MobileNavigationProps {
  user: User | null;
  pageTitle: string;
  hasAdminRole: boolean;
  userRole: string | null;
  unreadNotifications: number;
  tabVisibility: Record<string, boolean>;
  telegramConnected: boolean;
  hasApplications: boolean;
  creatorsWithoutSocials: string[];
  onLogout: () => void;
  onOpenTelegramModal: () => void;
  onOpenFeedbackModal: () => void;
}

interface NavSection {
  id: string;
  label: string;
  icon: any;
  items: NavItem[];
}

interface NavItem {
  id: string;
  label: string;
  icon: any;
  path: string;
  settingsKey?: string;
  badge?: string;
  disabled?: boolean;
}

const sidebarSections: NavSection[] = [
  {
    id: "home",
    label: "Home",
    icon: Home,
    items: [{ id: "home", label: "Home", icon: Home, path: "/home" }],
  },
  {
    id: "education",
    label: "Education",
    icon: GraduationCap,
    items: [
      { id: "education", label: "Education", icon: GraduationCap, path: "/education" },
      { id: "knowledge-bot", label: "Knowledge", icon: Bot, path: "/education/knowledge-bot" },
    ],
  },
  {
    id: "creators",
    label: "Creators",
    icon: Users,
    items: [{ id: "creators", label: "Your creators", icon: Users, path: "/creators" }],
  },
  {
    id: "creating",
    label: "Creating",
    icon: Video,
    items: [
      { id: "video", label: "Video", icon: Video, path: "/creating/video", settingsKey: "video", badge: "Coming soon" },
      { id: "duplicating", label: "Duplicate", icon: Copy, path: "/duplicating", settingsKey: "duplicating", badge: "Coming soon" },
    ],
  },
  {
    id: "tools",
    label: "Tools",
    icon: Wrench,
    items: [
      { id: "calendar", label: "Calendar", icon: CalendarDays, path: "/calendar", settingsKey: "calendar" },
      ...(FEATURE_FLAGS.dashboardTracking
        ? [{ id: "tracking", label: "Tracking", icon: BarChart3, path: "/tracking", badge: "Coming soon" }]
        : []),
      { id: "compass", label: "Compass", icon: Layout, path: "/compass", badge: FEATURE_FLAGS.dashboardCompass ? undefined : "Coming soon", disabled: !FEATURE_FLAGS.dashboardCompass },
      { id: "avatar", label: "Avatar", icon: User, path: "/avatar", badge: "Coming soon", disabled: true },
      { id: "sma", label: "SMA", icon: Bot, path: "/sma", settingsKey: "sma" },
      { id: "dm-automation", label: "DM automation", icon: Mail, path: "/dm-automation", settingsKey: "dm-automation" },
    ],
  },
];

export const MobileNavigation = ({
  user,
  pageTitle,
  hasAdminRole,
  userRole,
  unreadNotifications,
  tabVisibility,
  telegramConnected,
  hasApplications,
  creatorsWithoutSocials,
  onLogout,
  onOpenTelegramModal,
  onOpenFeedbackModal,
}: MobileNavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const getVisibleItems = (items: NavItem[]) => {
    return items.filter((item) => {
      if (!item.settingsKey) return true;
      return tabVisibility[item.settingsKey] !== false;
    });
  };

  const handleNavItemClick = (path: string, disabled?: boolean) => {
    if (disabled) return;
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <>
      <header className="fixed left-0 right-0 top-0 z-50 flex h-16 items-center justify-between border-b border-black/5 bg-white/95 px-4 backdrop-blur-md md:hidden">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <button className="-ml-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 transition-colors hover:bg-muted" aria-label="Open menu">
              <Menu className="h-5 w-5 text-foreground" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-full max-w-[324px] border-r border-black/5 bg-white p-0 shadow-[0_20px_60px_rgba(0,0,0,0.08)]" hideCloseButton>
            <div className="flex h-full flex-col bg-white">
              <div className="flex h-20 items-center justify-between border-b border-black/5 px-5">
                <div>
                  <h2 className="text-[15px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">Model AI Agency</h2>
                  <p className="text-xs text-[#86868b]">Client dashboard</p>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-2xl p-2 transition-colors hover:bg-[#f5f5f7]"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5 text-[#1d1d1f]" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto p-3">
                {sidebarSections.map((section) => {
                  const visibleItems = getVisibleItems(section.items);
                  if (visibleItems.length === 0) return null;

                  if (section.id === "tools") {
                    return (
                      <div key={section.id} className="mb-3 flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-[14px] font-medium tracking-[-0.015em] text-[#1d1d1f]">
                        <span className="flex items-center gap-2.5 font-medium">
                          <Wrench className="h-[16px] w-[16px] text-[#1d1d1f]" />
                          <span className="truncate">{section.label}</span>
                        </span>
                        <span className="rounded-full bg-[#fff4e8] px-2.5 py-0.5 text-[10px] font-medium text-[#c47a2c] ring-1 ring-[#f1d3ad]">Coming soon</span>
                      </div>
                    );
                  }

                  return visibleItems.map((item) => {
                    const itemPath = item.path.split("?")[0];
                    const isActive = location.pathname === itemPath || (itemPath === "/notifications" && location.pathname.startsWith("/notifications"));

                    return (
                      <button
                        key={item.id}
                        onClick={() => handleNavItemClick(item.path, item.disabled)}
                        className={`mb-1 flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-[14px] font-medium tracking-[-0.015em] transition-all ${
                          item.disabled
                            ? "cursor-not-allowed text-[#8e8e93]/70"
                            : isActive
                              ? "bg-primary/10 text-primary shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
                              : "text-[#6e6e73] hover:bg-[#f5f5f7] hover:text-[#1d1d1f]"
                        }`}
                        disabled={item.disabled}
                      >
                        <span className="flex items-center gap-3 truncate">
                          <span className={`flex h-9 w-9 items-center justify-center rounded-xl ring-1 ${
                            isActive
                              ? "bg-white text-primary ring-primary/10"
                              : "bg-[#f5f5f7] text-[#8e8e93] ring-black/5"
                          }`}>
                            <item.icon className="h-4 w-4" />
                          </span>
                          <span className="truncate">{item.label}</span>
                        </span>
                        {item.badge && (
                          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-[#8e8e93] ring-1 ring-black/5">
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  });
                })}
              </nav>

              {((hasApplications && !telegramConnected) || creatorsWithoutSocials.length > 0) && (
                <div className="space-y-2 border-t border-black/5 p-4">
                  {hasApplications && !telegramConnected && (
                    <button
                      onClick={() => {
                        setSidebarOpen(false);
                        onOpenTelegramModal();
                      }}
                      className="w-full rounded-2xl bg-amber-500/10 px-3.5 py-3 text-left text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-500/15"
                    >
                      Telegram connection required
                    </button>
                  )}
                  {creatorsWithoutSocials.length > 0 && (
                    <button
                      onClick={() => {
                        setSidebarOpen(false);
                        navigate("/creators");
                      }}
                      className="w-full rounded-2xl bg-red-500/10 px-3.5 py-3 text-left text-sm font-semibold text-red-700 transition-colors hover:bg-red-500/15"
                    >
                      Socials need to be completed
                    </button>
                  )}
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>

        <h1 className="text-base font-semibold text-foreground">{pageTitle}</h1>

        <Popover open={profileOpen} onOpenChange={setProfileOpen}>
          <PopoverTrigger asChild>
            <button className="flex h-10 w-10 min-h-[44px] min-w-[44px] items-center justify-center rounded-2xl bg-primary/10 shadow-sm transition-opacity hover:opacity-80" aria-label="User profile">
              <span className="text-sm font-semibold text-primary">AI</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="end" sideOffset={8}>
            <div className="border-b border-border p-4">
              <p className="text-[15px] font-semibold text-foreground">{user?.email?.split("@")[0] || "User"}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <div className="p-2">
              <Link to="/profile" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted" onClick={() => setProfileOpen(false)}>
                <Settings className="h-5 w-5 text-muted-foreground" />
                Settings
              </Link>
              <button
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted"
                onClick={() => {
                  setProfileOpen(false);
                  onOpenFeedbackModal();
                }}
              >
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
                Feedback
              </button>
              {hasAdminRole && (
                <Link
                  to="/admin/dashboard"
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted"
                  onClick={() => setProfileOpen(false)}
                >
                  <Shield className={`h-5 w-5 ${userRole === "admin" ? "text-red-500" : userRole === "editor" ? "text-blue-500" : "text-gray-500"}`} />
                  {userRole === "admin" ? "Admin View" : userRole === "editor" ? "Editor View" : "Viewer View"}
                </Link>
              )}
            </div>
            <div className="border-t border-border p-2">
              <button
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] text-red-500 transition-colors hover:bg-red-500/10"
                onClick={() => {
                  setProfileOpen(false);
                  onLogout();
                }}
              >
                <LogOut className="h-5 w-5" />
                Uitloggen
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </header>

      <div className="h-14 flex-shrink-0 md:hidden" />
    </>
  );
};
